import { PERIOD_GRANULARITY_VALUES, type PeriodGranularity } from '../period-granularity.js';

export { PERIOD_GRANULARITY_VALUES };

/**
 * Period enumeration helper.
 *
 * Converts a date window + granularity into an array of non-overlapping,
 * contiguous Period objects. Used by both the API (to label response columns)
 * and the SPA (to render the availability grid header).
 *
 * Granularities:
 * - DAY: one period per calendar day
 * - HALF_MONTH: each calendar month split at the 16th (1–15 and 16–EOM)
 * - MONTH: one period per full calendar month
 * - WEEK: ISO-week-aligned Mon–Sun periods (first/last period clamped to window)
 *
 * All dates are treated as UTC calendar dates (no time-zone shifts).
 */

export type { PeriodGranularity };

export interface DateWindow {
  from: Date;
  to: Date;
}

export interface Period {
  start: Date;
  end: Date;
}

/**
 * Returns UTC midnight for the given UTC year, month (1-based), day.
 */
function utcDate(year: number, month: number, day: number): Date {
  return new Date(Date.UTC(year, month - 1, day));
}

/**
 * Last day of the month for the given UTC year and month (1-based).
 */
function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Add `days` to a UTC date.
 */
function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
}

function enumerateDayPeriods(window: DateWindow): Period[] {
  const periods: Period[] = [];
  let current = new Date(
    Date.UTC(window.from.getUTCFullYear(), window.from.getUTCMonth(), window.from.getUTCDate()),
  );
  const end = new Date(
    Date.UTC(window.to.getUTCFullYear(), window.to.getUTCMonth(), window.to.getUTCDate()),
  );
  while (current <= end) {
    periods.push({ start: current, end: current });
    current = addDays(current, 1);
  }
  return periods;
}

/** Clamps [start, end] to [winFrom, winTo] and returns null if they don't overlap. */
function clampPeriod(start: Date, end: Date, winFrom: Date, winTo: Date): Period | null {
  if (end < winFrom || start > winTo) return null;
  return {
    start: start < winFrom ? winFrom : start,
    end: end > winTo ? winTo : end,
  };
}

/** Advances year/month by one month. */
function nextMonth(year: number, month: number): { year: number; month: number } {
  return month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
}

function enumerateHalfMonthPeriods(window: DateWindow): Period[] {
  const periods: Period[] = [];
  const winFrom = new Date(
    Date.UTC(window.from.getUTCFullYear(), window.from.getUTCMonth(), window.from.getUTCDate()),
  );
  const winTo = new Date(
    Date.UTC(window.to.getUTCFullYear(), window.to.getUTCMonth(), window.to.getUTCDate()),
  );

  let year = winFrom.getUTCFullYear();
  let month = winFrom.getUTCMonth() + 1; // 1-based

  while (utcDate(year, month, 1) <= winTo) {
    const eom = lastDayOfMonth(year, month);
    const h1 = clampPeriod(utcDate(year, month, 1), utcDate(year, month, 15), winFrom, winTo);
    if (h1) periods.push(h1);
    const h2 = clampPeriod(utcDate(year, month, 16), utcDate(year, month, eom), winFrom, winTo);
    if (h2) periods.push(h2);
    ({ year, month } = nextMonth(year, month));
  }

  return periods;
}

function enumerateMonthPeriods(window: DateWindow): Period[] {
  const periods: Period[] = [];
  const winFrom = new Date(
    Date.UTC(window.from.getUTCFullYear(), window.from.getUTCMonth(), window.from.getUTCDate()),
  );
  const winTo = new Date(
    Date.UTC(window.to.getUTCFullYear(), window.to.getUTCMonth(), window.to.getUTCDate()),
  );

  let year = winFrom.getUTCFullYear();
  let month = winFrom.getUTCMonth() + 1; // 1-based

  while (utcDate(year, month, 1) <= winTo) {
    const eom = lastDayOfMonth(year, month);
    const period = clampPeriod(utcDate(year, month, 1), utcDate(year, month, eom), winFrom, winTo);
    if (period) periods.push(period);
    ({ year, month } = nextMonth(year, month));
  }

  return periods;
}

/**
 * Returns the Monday of the ISO week containing `date` (UTC).
 */
function isoWeekMonday(date: Date): Date {
  const day = date.getUTCDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  // ISO: Mon=1, so offset from Monday = (day === 0 ? 6 : day - 1)
  const offsetToMonday = day === 0 ? 6 : day - 1;
  return addDays(date, -offsetToMonday);
}

function enumerateWeekPeriods(window: DateWindow): Period[] {
  const periods: Period[] = [];
  const winFrom = new Date(
    Date.UTC(window.from.getUTCFullYear(), window.from.getUTCMonth(), window.from.getUTCDate()),
  );
  const winTo = new Date(
    Date.UTC(window.to.getUTCFullYear(), window.to.getUTCMonth(), window.to.getUTCDate()),
  );

  // Start at the Monday of the ISO week containing winFrom (clamped to winFrom)
  let weekStart = isoWeekMonday(winFrom);
  if (weekStart < winFrom) weekStart = winFrom;

  while (weekStart <= winTo) {
    // Sunday of the ISO week containing weekStart
    const sunday = addDays(isoWeekMonday(weekStart), 6);
    const weekEnd = sunday > winTo ? winTo : sunday;
    periods.push({ start: weekStart, end: weekEnd });
    weekStart = addDays(sunday, 1); // next Monday
  }

  return periods;
}

/**
 * Enumerates non-overlapping periods covering the given window at the given granularity.
 * The first period starts at `window.from`; the last ends at `window.to`.
 */
export function enumeratePeriods(window: DateWindow, granularity: PeriodGranularity): Period[] {
  switch (granularity) {
    case 'DAY':
      return enumerateDayPeriods(window);
    case 'HALF_MONTH':
      return enumerateHalfMonthPeriods(window);
    case 'MONTH':
      return enumerateMonthPeriods(window);
    case 'WEEK':
      return enumerateWeekPeriods(window);
  }
}
